import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import FlexView from '@/modules/shared/components/FlexView'
import { DndRoot } from '@/modules/shared/components/DndRoot'
import { HTML5Backend as Backend } from 'react-dnd-html5-backend'

import { DATE_FORMAT } from '@/app/constants/date'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import { MinimalPaymentCountryModel } from '@/app/models/minimalPaymentCountryModel'
import {
  buildSyntheticEventsFromTmsData,
  constructTmsData,
} from '@/app/common/tmsDataHelper'

import {
  BusinessTripChartWrapperProps,
  GpsPointModel,
} from '@/modules/drivers/components/businessTripChart'
import BusinessTripDayChartNew from '@/modules/drivers/components/businessTripChart/new/BusinessTripDayChartNew'
import { SCENE_HEIGHT } from '@/modules/drivers/tabs/activities/components_new/utils/layout'
import { CustomDragLayer } from '@/modules/drivers/components/businessTripChart'
import { Loader } from '@/ui'
import {
  firstCountryAfter,
  toCountryChangePlaces,
} from '@/modules/drivers/components/businessTripChart/countryChangePlaces'
import { useBusinessTripChartViewStore } from '@/modules/drivers/stores/businessTripChartViewStore'

const HEIGHT: number = SCENE_HEIGHT
const HIDDEN_IN_SIMPLIFIED_VIEW = [] as const

const DAY_COLUMN_WIDTH = 100
const RIGHT_GAP = 24

const HOMELAND_COUNTRY_CODE = 'PL'

const newRow: EventModel = {
  tripEvent: TripEventType.NotSet,
  vehicleType: TripVehicleType.NotSet,
  city: '',
  comments: '',
  date: undefined,
  isTransit: false,
  registrationNumber: '',
  isChecked: false,
  lp: -1,
  country: { code: 'pl', name: 'Polska', nationId: 40, id: '1' },
}

const BusinessTripChartWrapperNew: React.FC<BusinessTripChartWrapperProps> = (
  props,
) => {
  const {
    isRestsOnBaseModeOn,
    companyId,
    driverId,
    tripId,
    readonly,
    groupMode,
    dateFrom,
    dateTo,
    events,
    addEvent,
    removeEvents,
    editEvent,
    moveEvent,
    hideDelegationMarkerPopups,
    markNewEvent,
    activities,
    breaks,
    cardInserts,
    dailyPartialRegularRests,
    dailyRests,
    dayActivities,
    outOfScopes,
    existingDelegations,
    tripEvents,
    partialBreaks,
    places,
    violations,
    weekRecompenses,
    weeklyReducedRests,
    weeklyRests,
    notes,
    countries,
    countriesWithMinimalPayment,
    getDriverActivities,
    isLoading: externalLoading,
    number,
    translate,
    startTime: controlledStartTime,
    onStartTimeChange,
    noDndProvider,
    apiTmsData,
    apiGpsData,
    apiTachographData,
    showGpsOnGraph,
  } = props

  const showFullChart = useBusinessTripChartViewStore((s) => s.showFullChart)

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const resizeTimeoutRef = React.useRef<any>(null)
  const lastFetchTimeRef = React.useRef(0)
  const pendingFetchRef = React.useRef<any>(null)
  const lastFetchedRange = React.useRef<{ start: string; end: string } | null>(
    null,
  )

  const [width, setWidth] = React.useState(0)
  const [numberOfCharts, setNumberOfCharts] = React.useState(8)
  const [internalStartTime, setInternalStartTime] =
    React.useState<Moment | null>(null)

  const startTime = controlledStartTime || internalStartTime

  const layerData = React.useMemo(() => {
    if (showFullChart) {
      return {
        cardInserts,
        places,
        dayActivities,
        violations,
        dailyRests,
        dailyPartialRegularRests,
        breaks,
        partialBreaks,
      }
    }
    return {
      cardInserts: HIDDEN_IN_SIMPLIFIED_VIEW,
      places: toCountryChangePlaces(places, startTime),
      dayActivities: HIDDEN_IN_SIMPLIFIED_VIEW,
      violations: HIDDEN_IN_SIMPLIFIED_VIEW,
      dailyRests: HIDDEN_IN_SIMPLIFIED_VIEW,
      dailyPartialRegularRests: HIDDEN_IN_SIMPLIFIED_VIEW,
      breaks: HIDDEN_IN_SIMPLIFIED_VIEW,
      partialBreaks: HIDDEN_IN_SIMPLIFIED_VIEW,
    }
  }, [
    showFullChart,
    startTime,
    cardInserts,
    places,
    dayActivities,
    violations,
    dailyRests,
    dailyPartialRegularRests,
    breaks,
    partialBreaks,
  ])
  const setStartTime = React.useCallback(
    (date: Moment) => {
      if (onStartTimeChange) {
        onStartTimeChange(date)
      }
      setInternalStartTime(date)
    },
    [onStartTimeChange],
  )
  const [highlighted] = React.useState<Array<boolean>>(Array(7).fill(false))
  const [, setTmpEvent] = React.useState<EventModel | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)

  const defineChartWidth = React.useCallback(() => {
    if (!wrapperRef.current) return

    const containerWidth =
      wrapperRef.current.clientWidth - DAY_COLUMN_WIDTH - RIGHT_GAP
    const MIN_DAY_WIDTH = 150
    const calculatedDays = Math.max(
      1,
      Math.min(8, Math.floor(containerWidth / MIN_DAY_WIDTH)),
    )

    setWidth(containerWidth)
    setNumberOfCharts(calculatedDays)
  }, [])

  const fetchActivities = async (startDate: Moment) => {
    if (!startDate || !companyId || !driverId) return

    const days = 31
    const adjustedStartDate = startDate.clone().add(-days, 'days')
    const adjustedEndDate = startDate.clone().add(days, 'days')

    if (lastFetchedRange.current) {
      const currentStart = moment(startDate)
      const currentEnd = moment(startDate).add(numberOfCharts, 'days')
      const fetchedStart = moment(lastFetchedRange.current.start, DATE_FORMAT)
      const fetchedEnd = moment(lastFetchedRange.current.end, DATE_FORMAT)

      if (currentStart >= fetchedStart && currentEnd <= fetchedEnd) {
        return
      }
    }

    setIsLoading(true)
    try {
      await getDriverActivities(
        companyId,
        String(driverId),
        adjustedStartDate,
        adjustedEndDate,
      )
      lastFetchedRange.current = {
        start: adjustedStartDate.format(DATE_FORMAT),
        end: adjustedEndDate.format(DATE_FORMAT),
      }
    } catch (error) {
      console.error('Error fetching activities:', error)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    if (controlledStartTime) return
    if (dateFrom) {
      const momentDate = moment.isMoment(dateFrom) ? dateFrom : moment(dateFrom)
      if (momentDate.isValid()) {
        const newStartTime = momentDate.clone().startOf('day')
        setStartTime(newStartTime)
      }
    }
  }, [dateFrom, controlledStartTime])

  React.useEffect(() => {
    defineChartWidth()
  }, [defineChartWidth])

  React.useEffect(() => {
    if (startTime && startTime.isValid() && companyId && driverId) {
      const COOLDOWN = 1000
      const now = Date.now()
      const timeSinceLastFetch = now - lastFetchTimeRef.current

      if (pendingFetchRef.current) {
        clearTimeout(pendingFetchRef.current)
        pendingFetchRef.current = null
      }

      if (timeSinceLastFetch >= COOLDOWN) {
        lastFetchTimeRef.current = now
        fetchActivities(startTime)
      } else {
        const delay = COOLDOWN - timeSinceLastFetch
        pendingFetchRef.current = setTimeout(() => {
          lastFetchTimeRef.current = Date.now()
          fetchActivities(startTime)
          pendingFetchRef.current = null
        }, delay)
      }
    }
    return () => {
      if (pendingFetchRef.current) {
        clearTimeout(pendingFetchRef.current)
      }
    }
  }, [startTime, companyId, driverId])

  React.useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
      resizeTimeoutRef.current = setTimeout(() => {
        defineChartWidth()
      }, 250)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [defineChartWidth])

  const navigateDays = React.useCallback(
    (value: number) => {
      if (!startTime) return
      const newDay = startTime.clone().add(value, 'days')
      setStartTime(newDay)
    },
    [startTime, setStartTime],
  )


  React.useEffect(() => {
    if (!startTime) return undefined

    let scrollAccumulator = 0
    const SCROLL_THRESHOLD = 50

    const handleWheel = (event: WheelEvent) => {
      const isPopupOpen = document.querySelector(
        '.delegation-marker, .delegation-marker-extended',
      )
      if (isPopupOpen) return

      event.preventDefault()

      scrollAccumulator += event.deltaY

      if (scrollAccumulator >= SCROLL_THRESHOLD) {
        navigateDays(1)
        scrollAccumulator = 0
      } else if (scrollAccumulator <= -SCROLL_THRESHOLD) {
        navigateDays(-1)
        scrollAccumulator = 0
      }
    }

    const wrapper = wrapperRef.current
    if (!wrapper) return undefined

    wrapper.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      wrapper.removeEventListener('wheel', handleWheel)
    }
  }, [startTime, navigateDays])

  const getDates = (): Array<Moment> => {
    if (!startTime || !startTime.isValid()) return []

    const dates: Array<Moment> = []
    let currentDate = startTime.clone()

    for (let i = 0; i < numberOfCharts; i++) {
      dates.push(currentDate)
      currentDate = currentDate.clone().add(1, 'days')
    }

    return dates
  }

  const handleAddEvent = (event: EventModel) => {
    if (event.tripEvent === TripEventType.ChangeCountry && event.date) {
      const country = firstCountryAfter(places, moment(event.date), countries)
      if (country) {
        event = { ...event, country: { ...country, id: String(country.id) } }
      }
    }
    setTmpEvent(event)
    addEvent(event)
  }

  const handleEditEvent = (event: EventModel, shouldEdit: boolean) => {
    if (event.temporary || event.lp === -1) {
      if (event.lp === -1) {
        addEvent(event)
        setTmpEvent(null)
      } else {
        setTmpEvent(event)
        editEvent(event, shouldEdit)
      }
    } else {
      if (shouldEdit || event.isNew) {
        editEvent(event, false)
      }
      setTmpEvent(null)
    }
  }

  const handleRemoveEvents = (eventsToRemove: Array<EventModel>) => {
    setTmpEvent(null)
    removeEvents(eventsToRemove)
  }

  const determineMinimalPaymentCountries = (
    eventsList: Array<EventModel>,
  ): Array<MinimalPaymentCountryModel> => {
    const minimalPaymentForCountries: Array<MinimalPaymentCountryModel> = []

    if (eventsList != null) {
      const orderedEvents = [...eventsList].sort((a, b) => {
        if (a.date < b.date) return -1
        if (a.date > b.date) return 1
        return 0
      })

    
      const isDomesticTrip = orderedEvents.every(
        (event) =>
          event.country == null ||
          event.country.code.toUpperCase() === HOMELAND_COUNTRY_CODE,
      )

      if (isDomesticTrip) {
        return minimalPaymentForCountries
      }

      let previous: EventModel | null = null
      if (orderedEvents.length > 0) {
        previous = orderedEvents[0]
      }

      for (let i = 1; i < orderedEvents.length; i++) {
        const shouldIncludePrevious =
          previous?.tripEvent === TripEventType.ChangeCountry || i === 1

        if (shouldIncludePrevious) {
          if (
            previous.country != null &&
            countriesWithMinimalPayment.includes(
              previous.country.code.toUpperCase(),
            )
          ) {
            minimalPaymentForCountries.push({
              isTransit: previous.isTransit,
              startTime: previous.date,
              endTime: orderedEvents[i].date,
              country: previous.country.name,
            })
          }
        }
        previous = orderedEvents[i]
      }
    }

    return minimalPaymentForCountries
  }

  const insertDelegationMarker = (date: Moment, eventType: TripEventType) => {
    handleAddEvent({ ...newRow, date: date, tripEvent: eventType })
  }

  const renderChart = (index: number, chartStartTime: Moment) => {
    const eventsList = events
    const minimalPaymentForCountries =
      determineMinimalPaymentCountries(eventsList)
    const hasBegin = eventsList.some((e) => e.tripEvent === TripEventType.Begin)
    const hasLoadingOrUnloading = eventsList.some(
      (e) =>
        e.tripEvent === TripEventType.Loading ||
        e.tripEvent === TripEventType.Unloading,
    )
    const eventsForTms =
      !hasBegin && hasLoadingOrUnloading
        ? [
            {
              tripEvent: TripEventType.Begin,
              date: (() => {
                const earliest = [...eventsList].sort((a, b) =>
                  a.date < b.date ? -1 : 1,
                )[0]?.date
                if (!earliest) return chartStartTime
                return moment.isMoment(earliest)
                  ? earliest.clone().subtract(1, 'millisecond')
                  : moment(earliest).subtract(1, 'millisecond')
              })(),
              lp: -1,
              country: { code: 'pl', name: 'Polska', nationId: 40, id: '1' },
            } as EventModel,
            ...eventsList,
          ]
        : eventsList

    const constructedTmsData = constructTmsData(eventsForTms, chartStartTime)

    const dayStart = chartStartTime
    const dayEnd = chartStartTime.clone().add(1, 'days')
    const eventDocNumbers = new Set(
      eventsList.filter((e) => e.docNumber).map((e) => e.docNumber),
    )
    const uncoveredApiTms = (apiTmsData || []).filter(
      (t) => !(t.docNumber && eventDocNumbers.has(t.docNumber)),
    )

    let apiTmsMarkers = []
    if (uncoveredApiTms.length > 0 && dateFrom && dateTo) {
      const syntheticEvents = buildSyntheticEventsFromTmsData(
        uncoveredApiTms,
        moment.isMoment(dateFrom) ? dateFrom : moment(dateFrom),
        moment.isMoment(dateTo) ? dateTo : moment(dateTo),
      )
      const pipelineResult = constructTmsData(syntheticEvents, chartStartTime)

      if (pipelineResult.length > 0) {
        apiTmsMarkers = pipelineResult
      } else {
        apiTmsMarkers = uncoveredApiTms
          .filter(
            (item) =>
              item.unloadingDate > dayStart && item.loadingDate < dayEnd,
          )
          .map((item) => ({
            ...item,
            displayedLoadingDate: item.loadingDate,
            displayedUnloadingDate: item.unloadingDate,
          }))
      }
    }

    const tmsDataItems = [...constructedTmsData, ...apiTmsMarkers]

    const gpsPoints: GpsPointModel[] = showGpsOnGraph
      ? [
          ...(apiGpsData || []).flatMap((item) => {
            const country = item.foreignCountry || item.destinationCountry
            const points: GpsPointModel[] = []
            if (
              item.arriveDate &&
              moment(item.arriveDate).isSame(chartStartTime, 'day')
            ) {
              points.push({
                date: moment(item.arriveDate),
                country,
                isArrival: true,
                source: 'gps',
              })
            }
            if (
              item.leaveDate &&
              moment(item.leaveDate).isSame(chartStartTime, 'day')
            ) {
              points.push({
                date: moment(item.leaveDate),
                country,
                isArrival: false,
                source: 'gps',
              })
            }
            return points
          }),
          ...(apiTachographData || [])
            .filter(
              (item) =>
                item.date && moment(item.date).isSame(chartStartTime, 'day'),
            )
            .map((item) => ({
              date: moment(item.date),
              country: item.country,
              isArrival: item.isArrival ?? false,
              source: 'tachograph' as const,
            })),
        ]
      : []

    return (
      <BusinessTripDayChartNew
        isRestsOnBaseModeOn={isRestsOnBaseModeOn}
        key={chartStartTime.format(DATE_FORMAT)}
        insertDelegationMarker={insertDelegationMarker}
        tripId={tripId}
        highlighted={highlighted[index]}
        first={index === 0}
        last={index === numberOfCharts - 1}
        isLoading={externalLoading || isLoading}
        moveEvent={moveEvent}
        readonly={readonly}
        countries={countries}
        addEvent={handleAddEvent}
        editEvent={handleEditEvent}
        removeEvents={handleRemoveEvents}
        hideDelegationMarkerPopups={hideDelegationMarkerPopups}
        markNewEvent={markNewEvent}
        events={[...eventsList]}
        notes={notes}
        translate={translate}
        width={width}
        height={HEIGHT}
        startTime={chartStartTime}
        activities={activities}
        {...layerData}
        weeklyRests={weeklyRests}
        weeklyReducedRests={weeklyReducedRests}
        weekRecompenses={weekRecompenses}
        minimalPaymentCountries={minimalPaymentForCountries}
        tmsData={tmsDataItems}
        gpsPoints={gpsPoints}
        outOfScopes={outOfScopes}
        tripEvents={tripEvents}
        existingDelegations={
          existingDelegations &&
          existingDelegations.filter((d) => d.delegationNumber !== number)
        }
        onRestClick={props.onRestClick}
      />
    )
  }

  const dates = getDates()

  const content = (
    <>
      {!noDndProvider && <CustomDragLayer />}
      <div
        ref={wrapperRef}
        id="ribbon"
        className="0 tw-flex tw-w-full tw-flex-1 tw-flex-col tw-overflow-hidden"
      >
        {(!startTime || externalLoading || isLoading) && (
          <Loader
            className="tw-py-16"
            size="lg"
            text={translate('gridPager.loadingText')}
            textPosition="bottom"
          />
        )}
        {startTime && !externalLoading && !isLoading && (
          <FlexView style={{ width: '100%', height: '100%' }} column>
            <div
              style={{ marginTop: 10 }}
              className={
                groupMode
                  ? 'grouped-delegation-charts-wrapper-collapsed tw-h-full'
                  : 'delegation-charts-wrapper-collapsed tw-h-full'
              }
            >
              {dates.map((d, i) => renderChart(i, d))}
            </div>
          </FlexView>
        )}
      </div>
    </>
  )

  if (noDndProvider) {
    return content
  }

  return <DndRoot backend={Backend}>{content}</DndRoot>
}

export default BusinessTripChartWrapperNew
